/**
 * Rincian absensi satu karyawan satu bulan + tombol "Lihat titik GPS" (Q35: titik GPS = bukti absensi; hak lihat
 * Owner + Kepala Cabang region — `attendance_service.GPS_READ_ROLES`). Tiap pembukaan titik TERCATAT di audit
 * (backend), dan layar menyebutnya sebelum tombol ditekan. Koordinat ditampilkan sebagai angka saja — tidak dikirim
 * ke layanan peta pihak ketiga.
 */
import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { apiErrorMessage } from "@/lib/inventoryApi";
import { ambilSemua } from "@/lib/ambilSemua";
import { formatDate, formatDateTime } from "@/lib/format";

// Kode status/sumber dari basis data → kata yang dibaca staf (K200 butir 6).
const KATA_SUMBER = { KIOS: "kios", MOBILE: "HP" };
const kataStatus = (s) => (s ? s.charAt(0) + s.slice(1).toLowerCase().replace(/_/g, " ") : "—");

export default function RincianAbsensiGps({ employeeId, bulan }) {
  const [baris, setBaris] = useState(null);
  const [titik, setTitik] = useState({});
  const [sibuk, setSibuk] = useState("");

  useEffect(() => {
    (async () => {
      const [y, m] = bulan.split("-").map(Number);
      const akhir = new Date(y, m, 0).getDate();
      try {
        const baca = (params) => api.get("/hr/attendance", { params });
        setBaris(await ambilSemua(baca, { employee_id: employeeId, date_from: `${bulan}-01`,
                                          date_to: `${bulan}-${String(akhir).padStart(2, "0")}` }, { ukuran: 50 }));
      } catch (e) { toast.error(apiErrorMessage(e, "Rincian absensi gagal dimuat.")); setBaris([]); }
    })();
  }, [employeeId, bulan]);

  const lihat = async (a) => {
    setSibuk(a.id);
    try {
      const r = await api.get(`/hr/attendance/${a.id}/gps`);
      setTitik((t) => ({ ...t, [a.id]: r.data.items || [] }));
    } catch (e) { toast.error(apiErrorMessage(e, "Titik GPS gagal dibuka.")); }
    finally { setSibuk(""); }
  };

  if (baris === null) return <div className="px-3 py-2 text-sm text-muted-foreground">Memuat rincian…</div>;
  if (baris.length === 0) return <div className="px-3 py-2 text-sm text-muted-foreground">Tidak ada catatan di bulan ini.</div>;
  return (
    <div className="px-3 py-2 space-y-2 text-sm" data-testid={`rincian-absensi-${employeeId}`}>
      <p className="text-xs text-muted-foreground">Membuka titik GPS tercatat di jejak audit (siapa, kapan, milik siapa).</p>
      {baris.map((a) => (
        <div key={a.id} className="border-t border-border pt-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-medium">{formatDate(a.attendance_date)}</span>
            <span className="text-muted-foreground">{kataStatus(a.status)} · {KATA_SUMBER[a.source] || "—"}</span>
            {a.check_in_at && <span>masuk {formatDateTime(a.check_in_at)}</span>}
            {a.check_out_at && <span>pulang {formatDateTime(a.check_out_at)}</span>}
            {a.source === "MOBILE" && !titik[a.id] && (
              <button type="button" className="underline" disabled={sibuk === a.id} onClick={() => lihat(a)}
                      data-testid={`gps-lihat-${a.id}`}>
                {sibuk === a.id ? "Membuka…" : "Lihat titik GPS"}
              </button>
            )}
          </div>
          {titik[a.id] && (titik[a.id].length === 0 ? (
            <div className="text-xs text-muted-foreground">Tidak ada titik (lebih dari 90 hari sudah dihapus retensi).</div>
          ) : (
            <table className="text-xs mt-1" data-testid={`gps-titik-${a.id}`}>
              <tbody>
                {titik[a.id].map((t) => (
                  <tr key={t.id}>
                    <td className="pr-3">{formatDateTime(t.captured_at)}</td>
                    <td className="pr-3 tabular-nums">{t.lat}, {t.lng}</td>
                    <td className="text-muted-foreground">{t.accuracy_m != null ? `±${Math.round(t.accuracy_m)} m` : ""}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ))}
        </div>
      ))}
    </div>
  );
}
